import { MaskitoOptions } from "@maskito/core";

/**
 * Maskito mask options for a valid percentage input up to two decimal places
 * @returns Maskito mask options
 */
export const validPercentageMask: MaskitoOptions = {
  mask: ({ value }) => {
    // Remove the % symbol for processing
    const cleanValue = value.replaceAll("%", "");

    // Special case for 100% - handle it completely separately
    if (cleanValue === "100") {
      return [/\d/, /\d/, /\d/, "%"];
    }
    // If we already have 100, prevent any more digits
    if (cleanValue.startsWith("100")) {
      return [/\d/, /\d/, /\d/, "%"];
    }

    // Split into whole and decimal parts
    const [whole, decimal] = cleanValue.split(".");

    // Handle whole number part
    const wholeDigits = whole ? Array.from(whole).map(() => /\d/) : [];

    // Build the mask array starting with whole number digits
    const mask: (RegExp | string)[] = [];

    // Handle first two digits
    if (wholeDigits.length > 0) {
      mask.push(/\d/);
      if (wholeDigits.length > 1) {
        mask.push(/\d/);
      }
    }

    // If we have a decimal point or decimal digits, add them
    if (cleanValue.includes(".")) {
      mask.push(".");
      if (decimal) {
        mask.push(...Array.from(decimal.slice(0, 2)).map(() => /\d/));
      }
    }
    // If we have 3 digits and they're not 100, add decimal point
    else if (whole && whole.length >= 3) {
      mask.push(".");
      // Add the third digit after the decimal point
      mask.push(/\d/);
    }

    // Add % symbol at the end
    mask.push("%");

    return mask;
  },
};

/**
 * Maskito mask options for a valid integer percentage input
 * @returns Maskito mask options
 */
export const integerPercentageMask: MaskitoOptions = {
  mask: ({ value }) => {
    // Remove the % symbol for processing
    const cleanValue = value.replaceAll("%", "");

    // If we have 100, only allow 100%
    if (cleanValue === "100") {
      return [/\d/, /\d/, /\d/, "%"];
    }

    // If we already have 100, prevent any more digits
    if (cleanValue.startsWith("100")) {
      return [/\d/, /\d/, /\d/, "%"];
    }

    // For other values, allow up to 2 digits
    const digits = cleanValue.length;
    if (digits === 0) return [/\d/, /\d/, "%"];
    if (digits === 1) return [/\d/, /\d/, "%"];
    if (digits === 2) return [/\d/, /\d/, "%"];

    return [/\d/, /\d/, "%"];
  },
};
